"use client";

import {
  FC,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import clsx from "clsx";
import { Tabs } from "@mantine/core";
import { ChevronLeft, ChevronRight } from "react-feather";

import TButton from "@/components/atoms/buttons/TButton";
import HtmlText from "@/sections/common/HtmlText";

import { ITextButton } from "@/components/atoms/buttons/types";

// ---------------------------------------------------------------------------------------

export type IPenetrationTabs = {
  heading?: string;
  description?: ReactNode;
  badges?: ITextButton[];
};

export type IPenetrationCasesSectionProps = HTMLAttributes<HTMLElement> & {
  heading?: string;
  tabs?: IPenetrationTabs[];
};

// Cross-browser scrollbar-hide utility (no Tailwind plugin installed).
// Hides the scrollbar but preserves horizontal scrolling on mobile.
const HIDE_SCROLLBAR =
  "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden";

const PenetrationCasesSection: FC<IPenetrationCasesSectionProps> = (props) => {
  const { heading, tabs, className, ...other } = props;

  const [activeValue, setActiveValue] = useState(
    tabs ? (tabs[0].heading ?? "0") : "",
  );

  // Refs + state to detect overflow and scroll position. Drives the
  // chevron hint (only shows when there's more content to scroll to)
  // and lets us auto-scroll the active tab into view on selection.
  const listRef = useRef<HTMLDivElement | null>(null);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);

  const updateScrollState = () => {
    const el = listRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateScrollState();
    const el = listRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      ro.disconnect();
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [tabs]);

  // Center the active tab horizontally within the scrollable tab list
  // when it changes.
  //
  // Previously used `element.scrollIntoView({ block: "nearest" })` which
  // — despite the name — scrolls the entire PAGE vertically whenever the
  // tab list is below the fold. On industry pages this fired on mount
  // (because activeValue initializes to the first tab) and jumped the
  // visitor past the hero straight into this section.
  //
  // Fix: manually scroll only the tab list's horizontal scroll position
  // by calculating the active button's center relative to the container.
  // Page vertical scroll is left alone. Also skip the initial mount run
  // — the tab list already starts at scrollLeft=0 with the first tab
  // visible, so there's nothing to center.
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    const el = listRef.current;
    if (!el) return;
    const activeBtn = el.querySelector<HTMLElement>('[data-active="true"]');
    if (!activeBtn) return;
    const target =
      activeBtn.offsetLeft - el.clientWidth / 2 + activeBtn.clientWidth / 2;
    el.scrollTo({ left: target, behavior: "smooth" });
  }, [activeValue]);

  return (
    <section
      className={clsx(
        "py-10 dark:bg-black-900 md:py-14 lg:py-16 xl:py-20",
        className,
      )}
      {...other}
    >
      <div className="container">
        {heading && (
          // Canonical SectionHeader title clamp (22px → 48px) — same as
          // every other section across the site.
          <h2 className="mb-8 text-center font-bricolage-grotesque text-[clamp(1.375rem,3vw+0.75rem,3rem)] font-medium !leading-[1.1] text-black-900 dark:text-white md:mb-10 lg:mb-12">
            {heading}
          </h2>
        )}

        {tabs && tabs.length > 0 && (
          <Tabs
            value={activeValue}
            onChange={(value) => value !== null && setActiveValue(value)}
          >
            {tabs[0].heading && (
              // Wrapper handles overflow + edge-fade indicators + the
              // chevron hint. All three signals together make the
              // "scrollable" affordance unambiguous on mobile.
              <div className="relative mx-auto mb-8 w-fit max-w-full md:mb-10 lg:mb-12">
                {/* Edge fades — only render when there's actually more
                    content to scroll to. Avoids showing a misleading
                    fade when the bar already fits the viewport. */}
                {canScrollLeft && (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-y-0 left-0 z-10 flex w-10 items-center justify-start rounded-l-full bg-gradient-to-r from-white via-white to-transparent pl-2 dark:from-black-900 dark:via-black-900"
                  >
                    <ChevronLeft
                      size={18}
                      className="animate-scroll-hint-left text-primary-900"
                    />
                  </div>
                )}
                {canScrollRight && (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-y-0 right-0 z-10 flex w-10 items-center justify-end rounded-r-full bg-gradient-to-l from-white via-white to-transparent pr-2 dark:from-black-900 dark:via-black-900"
                  >
                    {/* Bouncing chevron — explicit visual cue that more
                        tabs exist to the right. The animation only
                        renders on mobile-ish widths where horizontal
                        scroll is the primary interaction. */}
                    <ChevronRight
                      size={18}
                      className="animate-scroll-hint text-primary-900"
                    />
                  </div>
                )}

                <Tabs.List
                  ref={listRef as never}
                  className={clsx(
                    // Pill-style segmented control — clearly reads as
                    // "tabs" (vs the previous bare underline that looked
                    // like static headings). Hugs content on wide
                    // screens; falls back to horizontal scroll when
                    // there's more than the viewport can fit.
                    "relative flex w-fit max-w-full flex-nowrap gap-2 overflow-x-auto scroll-smooth rounded-full border border-black-100 bg-black-50 p-1.5 before:hidden md:gap-1 dark:border-black-700 dark:bg-black-800",
                    HIDE_SCROLLBAR,
                  )}
                >
                  {tabs.map(
                    (tab, idx) =>
                      tab.heading && (
                        <Tabs.Tab
                          key={idx}
                          value={tab.heading ?? String(idx)}
                          // data-active drives the scroll-center effect
                          data-active={tab.heading === activeValue}
                          className={clsx(
                            // Responsive padding + clamp font so the tab
                            // labels scale fluidly between phones and
                            // desktop instead of jumping at md.
                            "shrink-0 cursor-pointer whitespace-nowrap rounded-full !border-0 px-3.5 py-2 font-bricolage-grotesque text-[clamp(0.75rem,0.25vw+0.7rem,1rem)] font-normal transition-all duration-200 sm:px-4 sm:py-2.5 md:px-5 md:py-3",
                            tab.heading === activeValue
                              ? "bg-primary-900 text-white shadow-md hover:bg-primary-800"
                              : "text-black-700 hover:bg-white hover:text-primary-900 hover:shadow-sm dark:text-black-200 dark:hover:bg-black-700 dark:hover:text-white",
                          )}
                        >
                          {tab.heading}
                        </Tabs.Tab>
                      ),
                  )}
                </Tabs.List>
              </div>
            )}

            {tabs.map((tab, idx) => (
              <Tabs.Panel
                key={idx}
                value={tab.heading ?? String(idx)}
                className="mx-auto flex flex-col gap-6 px-2 sm:px-4 md:gap-8 lg:max-w-[994px] lg:px-0"
              >
                {tab.description && (
                  <HtmlText className="prose-p:text-center prose-p:!text-[clamp(0.875rem,0.5vw+0.75rem,1.125rem)]">
                    {tab.description}
                  </HtmlText>
                )}

                {tab.badges && tab.badges.length > 0 && (
                  <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 md:gap-3.5">
                    {tab.badges.map((badge, idx) => (
                      <TButton
                        key={idx}
                        id={badge.id}
                        variant={badge.variant}
                        href={badge.link}
                        radius={badge.radius}
                        target={badge.target}
                        classNames={{ label: "whitespace-normal" }}
                        // Override the TButton/light_primary defaults with
                        // canonical Bricolage Grotesque + clamp typography
                        // so the pill labels match the rest of the site.
                        // !-prefixed where Mantine/CVA defaults compete.
                        className="!h-auto !rounded-full !border-0 !bg-primary-100 !px-3 !py-1.5 !font-bricolage-grotesque !text-[clamp(0.75rem,0.25vw+0.7rem,0.9375rem)] !font-normal !text-primary-900 hover:!bg-primary-900 hover:!text-white sm:!px-4 sm:!py-2 md:!px-5 md:!py-2.5"
                      >
                        {badge.text}
                      </TButton>
                    ))}
                  </div>
                )}
              </Tabs.Panel>
            ))}
          </Tabs>
        )}
      </div>
    </section>
  );
};

export default PenetrationCasesSection;
